import type {
  AddEvent,
  InboundEmail,
  ListItem,
  Order,
  Person,
  PersonId,
  PricePoint,
  Segment,
  SegmentChange,
  Staple,
  StoreProduct,
  StoreSettings,
  Trip,
} from '@shared/types.ts';
import type { FeatureRow } from '@shared/features.ts';
import type { Note } from '@shared/notes.ts';

export interface AppSettings {
  /** Minutes the list must be quiet before carts are prepared. */
  quietMinutes: number;
  inboxAddress: string;
  /** IATA codes / city names treated as home when naming trips. */
  homeCodes: string[];
}

export interface AppState {
  people: Person[];
  /** Who is using this device (local mode) or the signed-in member (Supabase mode). */
  me?: PersonId;
  list: ListItem[];
  staples: Staple[];
  products: StoreProduct[];
  stores: StoreSettings[];
  orders: Order[];
  events: AddEvent[];
  prices: PricePoint[];
  trips: Trip[];
  segments: Segment[];
  changes: SegmentChange[];
  emails: InboundEmail[];
  /** Feature switches (Settings → Developer). */
  features: FeatureRow[];
  /** Feedback notes (Michael only; empty for anyone else). */
  notes: Note[];
  settings: AppSettings;
  /** When carts were last prepared (server job or this device). */
  lastPreparedAt?: string;
}

export const PEOPLE: Person[] = [
  { id: 'michael', name: 'Michael', initial: 'M', bookingNames: ['Michael Lei Tan'] },
  { id: 'natasha', name: 'Natalie', initial: 'N', bookingNames: ['Natalie Aleksandra Shevchenko'] },
];

export const DEFAULT_STORES: StoreSettings[] = [
  {
    id: 'amazon',
    name: 'Amazon',
    enabled: true,
    priority: 1,
    minimumOrder: 0,
    freeDeliveryThreshold: 0,
    feeBelowThreshold: 0,
    maxUniqueItems: null,
    deliveryHours: 24,
    cartFill: 'amazon-link',
    carries: 'pantry',
  },
  {
    id: 'amazonfresh',
    name: 'Amazon Fresh',
    enabled: true,
    priority: 2,
    minimumOrder: 0,
    freeDeliveryThreshold: 25,
    feeBelowThreshold: 9.99,
    maxUniqueItems: null,
    deliveryHours: 4,
    cartFill: 'amazon-fresh-link',
    carries: 'perishable',
  },
  {
    id: 'costco',
    name: 'Costco Same-Day',
    enabled: true,
    priority: 3,
    minimumOrder: 35,
    freeDeliveryThreshold: 35,
    feeBelowThreshold: 0,
    maxUniqueItems: null,
    deliveryHours: 4,
    cartFill: 'instacart-link',
    carries: 'any',
  },
  {
    id: 'wholefoods',
    name: 'Whole Foods',
    enabled: false,
    priority: 4,
    minimumOrder: 0,
    freeDeliveryThreshold: 0,
    feeBelowThreshold: 9.95,
    maxUniqueItems: 75,
    deliveryHours: 3,
    cartFill: 'checklist',
    carries: 'any',
  },
];

export function emptyState(inboxAddress: string): AppState {
  return {
    people: PEOPLE,
    list: [],
    staples: [],
    products: [],
    stores: DEFAULT_STORES,
    orders: [],
    events: [],
    prices: [],
    trips: [],
    segments: [],
    changes: [],
    emails: [],
    features: [],
    notes: [],
    settings: { quietMinutes: 10, inboxAddress, homeCodes: ['JFK', 'LGA', 'EWR'] },
  };
}
